/** @graphql agg **/
import { graphql } from 'react-relay';

import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type {
	preloadIncomingLinksSampleQuery$data,
	preloadIncomingLinksSampleQuery$variables,
} from './__generated__/preloadIncomingLinksSampleQuery.graphql.ts';
import {
	buildIncomingLinksSampleVariables,
	normalizeIncomingLinksSampleData,
	type IncomingLinksSampleResult,
	type IncomingLinksSampleUnavailableReason,
} from './incomingLinksSsrState.ts';

type IncomingLinksSampleCauseCategory = 'error' | 'graphql' | 'none' | 'non-error';

// The bounded incoming-links sample: up to `INCOMING_LINKS_SAMPLE_LIMIT` source ARIs that reference
// this page. It warms the AGG query cache so the client can read the same query `cache-only` after
// SSR hydration, mirroring the outbound byline preload.
const IncomingLinksSampleQuery = graphql`
	query preloadIncomingLinksSampleQuery($cypherQuery: String!, $first: Int!) {
		graphStore_cypherQueryV2(query: $cypherQuery, first: $first)
			@optIn(to: "GraphStoreCypherQueryV2") {
			edges {
				node {
					columns {
						key
						value {
							__typename
							... on GraphStoreCypherQueryV2AriNode {
								id
							}
						}
					}
				}
			}
		}
	}
`;

const unavailable = (reason: IncomingLinksSampleUnavailableReason): IncomingLinksSampleResult => ({
	status: 'unavailable',
	reason,
});

const reportUnavailable = (
	reason: Exclude<IncomingLinksSampleUnavailableReason, 'invalid-input'>,
	cause?: unknown,
) => {
	const causeCategory: IncomingLinksSampleCauseCategory =
		cause == null
			? 'none'
			: cause instanceof Error
				? cause.name === 'GraphQLError'
					? 'graphql'
					: 'error'
				: 'non-error';

	ssrPrepareErrorsClient.submitError(
		new Error(`Incoming links sample unavailable: ${reason}`, { cause }),
		{
			operation: 'preload-incoming-links-sample',
			reason,
			causeCategory,
		},
	);
};

export const preloadIncomingLinksSample = async ({
	cloudId,
	contentId,
}: {
	cloudId: string;
	contentId: string;
}): Promise<IncomingLinksSampleResult> => {
	const variables = buildIncomingLinksSampleVariables(cloudId, contentId);
	if (!variables) {
		return unavailable('invalid-input');
	}

	try {
		// No `no-cache`: the sample warms the AGG query cache so the client can read the same query
		// `cache-only` after SSR hydration, mirroring the outbound byline preload.
		const { data, errors, hasErrors } = await queryAgg<
			preloadIncomingLinksSampleQuery$data,
			preloadIncomingLinksSampleQuery$variables
		>({
			query: IncomingLinksSampleQuery,
			variables,
			context: {
				headers: {
					'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
				},
			},
		});

		if (hasErrors || errors?.length) {
			reportUnavailable('query-error', errors?.[0]);
			return unavailable('query-error');
		}

		const result = normalizeIncomingLinksSampleData(data);
		if (result.status === 'unavailable') {
			reportUnavailable('malformed-response');
		}
		return result;
	} catch (cause) {
		reportUnavailable('query-error', cause);
		return unavailable('query-error');
	}
};
